import { signUp, confirmSignUp, resendSignUpCode, signIn, fetchAuthSession } from '../../util/amplifyAuth';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { saveUserProfile, acceptTerms } from '../API';
import { setCachedProfile } from '../../util/CacheUtil';
import Spinner from '../Spinner/Spinner';
import PasswordInput from '../PasswordInput';
import PasswordRequirements, { usePasswordValidation } from '../PasswordRequirements';
import ProfileForm from '../ProfileForm/ProfileForm';
import useTestimonials from '../../hooks/useTestimonials';
import '../../styles/Signup.css';
import '../../styles/Verify.css';
import '../../styles/PasswordInput.css';
import './SignupFlow.css';

function SignupFlow() {
    const [step, setStep] = useState(1);

    // Step 1 state
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    // Step 1 terms
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Step 2 state
    const [code, setCode] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [resending, setResending] = useState(false);

    // Shared state
    const [formError, setFormError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const textReviews = useTestimonials();
    const redirectParam = searchParams.get('redirect');

    const { valid: passwordValid } = usePasswordValidation(password);

    // ========================================
    // Step 1: Create Account
    // ========================================
    const handleSignUp = async (e) => {
        e.preventDefault();
        setFormError('');

        if (!email) { setFormError('Please enter your email address.'); return; }
        if (!passwordValid) { setFormError('Please meet all password requirements.'); return; }
        if (password !== confirmPassword) { setFormError('Passwords do not match.'); return; }

        setSubmitting(true);
        try {
            await signUp({
                username: email,
                password,
                options: {
                    userAttributes: { email },
                },
            });
            setFormError('');
            setStep(2);
        } catch (err) {
            setFormError(
                err.message +
                '\n\nIf your clinic has already set up an account for you, check your email for an activation link.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // ========================================
    // Step 2: Verify Email
    // ========================================
    const handleVerify = async (e) => {
        e.preventDefault();
        setFormError('');
        setSuccessMessage('');

        if (!code.trim()) { setFormError('Please enter the verification code.'); return; }

        setSubmitting(true);
        try {
            await confirmSignUp({ username: email, confirmationCode: code });
            try {
                await signIn({ username: email, password });
                await acceptTerms().catch(() => {});
                setFormError('');
                setStep(3);
            } catch {
                // signIn failed — fall back to login
                navigate(redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : '/login');
            }
        } catch (err) {
            setFormError(err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const handleResendCode = async () => {
        setFormError('');
        setSuccessMessage('');
        setResending(true);
        try {
            await resendSignUpCode({ username: email });
            setSuccessMessage('Verification code resent successfully!');
        } catch (err) {
            setFormError(err.message);
        } finally {
            setResending(false);
        }
    };

    // ========================================
    // Step 3: Complete Profile
    // ========================================
    const handleProfileSave = async (profileData) => {
        setSubmitting(true);
        try {
            const result = await saveUserProfile(profileData);
            setCachedProfile(result.profile || profileData);
            await fetchAuthSession({ forceRefresh: true });
            navigate(redirectParam || '/accept-terms');
        } catch (err) {
            setFormError('Error saving profile: ' + err.message);
            setSubmitting(false);
        }
    };

    // ========================================
    // Render
    // ========================================
    return (
        <div className="signup-page">
            <div className="signup-overlay">
                <div className="signup-modal">
                    {/* Step indicator */}
                    <div className="signup-step-indicator">
                        <div className={`signup-step-dot ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`} />
                        <div className={`signup-step-dot ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`} />
                        <div className={`signup-step-dot ${step >= 3 ? 'active' : ''}`} />
                    </div>

                    {/* Step 1: Account */}
                    {step === 1 && (
                        <div className="signup-flow-step" key="step-1">
                            <h1>Create Your Account</h1>
                            <p className="signup-subtitle">
                                Start decoding your blood work with personalized, science-backed insights.
                            </p>

                            {formError && (
                                <div className="signup-error" style={{ whiteSpace: 'pre-line' }}>
                                    {formError}
                                </div>
                            )}

                            <form className="signup-form" onSubmit={handleSignUp}>
                                <div className="form-group">
                                    <label htmlFor="signup-email">Email</label>
                                    <input
                                        id="signup-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                    />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="signup-password">Password</label>
                                    <PasswordInput
                                        id="signup-password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                    />
                                    <PasswordRequirements password={password} />
                                </div>

                                <div className="form-group">
                                    <label htmlFor="signup-confirm">Confirm Password</label>
                                    <PasswordInput
                                        id="signup-confirm"
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                    />
                                </div>

                                <label className="signup-terms-label">
                                    <input
                                        type="checkbox"
                                        checked={termsAccepted}
                                        onChange={(e) => setTermsAccepted(e.target.checked)}
                                    />
                                    <span>
                                        I agree to the{' '}
                                        <a href="/legal/data-protection" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                                        {' '}and{' '}
                                        <a href="/legal/service-agreement" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                                    </span>
                                </label>

                                <button className="signup-submit" type="submit" disabled={submitting || !termsAccepted}>
                                    {submitting ? <Spinner size={20} color="#fff" /> : 'Create Account'}
                                </button>
                            </form>

                            <hr className="signup-divider" />

                            <p className="signup-login-link">
                                Already have an account?{' '}
                                <a href="/login" onClick={(e) => {
                                    e.preventDefault();
                                    navigate(redirectParam ? `/login?redirect=${encodeURIComponent(redirectParam)}` : '/login');
                                }}>Log in</a>
                            </p>

                            <div className="signup-testimonials-ticker">
                                <div className="ticker-track">
                                    {[...textReviews, ...textReviews].map((review, i) => (
                                        <div className="ticker-card" key={i}>
                                            <p>"{review.quote}"</p>
                                            <span>— {review.author}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Step 2: Verify */}
                    {step === 2 && (
                        <div className="signup-flow-step" key="step-2">
                            <h1>Verify Your Account</h1>
                            <p className="signup-subtitle">
                                Enter the verification code sent to{' '}
                                <strong>{email}</strong>.
                            </p>

                            {formError && <div className="signup-error">{formError}</div>}
                            {successMessage && (
                                <div className="verify-success">{successMessage}</div>
                            )}

                            <form className="verify-form" onSubmit={handleVerify}>
                                <div className="form-group">
                                    <label htmlFor="verify-code">Verification Code</label>
                                    <input
                                        id="verify-code"
                                        type="text"
                                        inputMode="numeric"
                                        autoComplete="one-time-code"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                    />
                                </div>

                                <button className="verify-submit" type="submit" disabled={submitting}>
                                    {submitting ? <Spinner size={20} color="#fff" /> : 'Verify Account'}
                                </button>

                                <button
                                    className="verify-resend"
                                    type="button"
                                    onClick={handleResendCode}
                                    disabled={resending}
                                >
                                    {resending ? <Spinner size={20} color="#111827" /> : 'Resend Code'}
                                </button>
                            </form>

                            <hr className="signup-divider" />

                            <p className="signup-login-link">
                                Back to{' '}
                                <a href="#" onClick={(e) => { e.preventDefault(); setStep(1); setFormError(''); }}>Sign up</a>
                            </p>
                        </div>
                    )}

                    {/* Step 3: Profile */}
                    {step === 3 && (
                        <div className="signup-flow-step" key="step-3">
                            <h1>Complete Your Profile</h1>
                            <p className="signup-subtitle">
                                Tell us a little about yourself so we can personalize your results.
                            </p>

                            {formError && (
                                <div className="signup-error">{formError}</div>
                            )}

                            <ProfileForm
                                onSave={handleProfileSave}
                                submitting={submitting}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default SignupFlow;
